import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Beaker, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import SectionLabel from "@/components/SectionLabel";

export default function IngredientDetail() {
  const { slug } = useParams();
  const [ingredients, setIngredients] = useState([]);

  useEffect(() => {
    api.products().then((d) => setIngredients(d.ingredients || [])).catch(() => {});
  }, []);

  const ing = ingredients.find((i) => i.slug === slug);
  const related = ingredients.filter((i) => i.slug !== slug).slice(0, 4);

  useEffect(() => {
    if (!ing) return;
    const desc = (ing.study?.summary || ing.detail || ing.benefit || "").slice(0, 300);
    const title = `${ing.name} — dose, benefits & research | Vitura`;
    const prevTitle = document.title;
    document.title = title;

    const upsertMeta = (attr, key, content) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      const created = !el;
      const prev = el ? el.getAttribute("content") : null;
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute("content", content);
      return () => { if (created) el.remove(); else if (prev !== null) el.setAttribute("content", prev); };
    };

    const restorers = [
      upsertMeta("name", "description", desc),
      upsertMeta("property", "og:title", title),
      upsertMeta("property", "og:description", desc),
      upsertMeta("property", "og:type", "article"),
    ];
    if (ing.image) restorers.push(upsertMeta("property", "og:image", ing.image));

    let canon = document.head.querySelector('link[rel="canonical"]');
    const canonCreated = !canon;
    const prevCanon = canon ? canon.getAttribute("href") : null;
    if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
    canon.setAttribute("href", window.location.href);

    const ldObj = {
      "@context": "https://schema.org",
      "@type": "DietarySupplement",
      "name": ing.name,
      "description": desc,
      "category": ing.category,
      "activeIngredient": ing.name,
      "brand": { "@type": "Brand", "name": "Vitura" },
      "isPartOf": { "@type": "Product", "name": "Vitura Performance\u2122 Blend" },
      "url": window.location.href,
    };
    if (ing.image) ldObj.image = ing.image;
    if (ing.study) ldObj.citation = { "@type": "CreativeWork", "name": ing.study.label, "url": ing.study.url };
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-ingredient-ld", "true");
    ld.text = JSON.stringify(ldObj);
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      restorers.forEach((r) => r && r());
      if (canonCreated) canon.remove(); else if (prevCanon !== null) canon.setAttribute("href", prevCanon);
      ld.remove();
    };
  }, [ing]);

  if (!ingredients.length) {
    return <div className="max-w-site mx-auto px-6 md:px-10 py-24 label !text-[11px]">Loading…</div>;
  }
  if (!ing) {
    return (
      <div className="max-w-site mx-auto px-6 md:px-10 py-24" data-testid="ingredient-not-found">
        <p className="font-display text-3xl">Ingredient not found.</p>
        <Link to="/ingredients" className="mt-4 inline-flex items-center gap-2 label !text-[11px] underline">Back to the library</Link>
      </div>
    );
  }

  return (
    <div className="max-w-site mx-auto px-6 md:px-10 py-14 md:py-20" data-testid="ingredient-detail">
      <Link to="/ingredients" className="inline-flex items-center gap-2 label !text-[10px] !tracking-[0.18em] text-[color:var(--ink-3)] hover:text-[color:var(--ink)] transition" data-testid="ingredient-back">
        <ArrowLeft size={13} strokeWidth={1.6} /> The Library
      </Link>

      {ing.image && (
        <div className="mt-6 relative h-52 md:h-72 overflow-hidden border border-[color:var(--line)]" data-testid="ingredient-hero-image">
          <img src={ing.image} alt={ing.name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f10]/55 to-transparent" />
          <div className="absolute bottom-4 left-5 label !text-[10px] !text-[#faf9f5]/80">{ing.category}</div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mt-8">
        {/* Left: identity */}
        <div className="lg:col-span-7">
          <SectionLabel index={`Active · ${ing.category}`}>Clinical breakdown.</SectionLabel>
          <motion.h1
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] leading-[0.98] mt-6"
          >
            {ing.name}
          </motion.h1>

          <div className="mt-6 flex items-center gap-4">
            {ing.show_dose ? (
              <span className="font-mono tabular-nums text-sm font-semibold px-2.5 py-1 bg-[#a8c69f]/30 text-[#3a4a32] border border-[#a8c69f]/70">{ing.dose} per serving</span>
            ) : (
              <span className="inline-flex items-center gap-2 label !text-[10px] !text-[color:var(--ink-3)]">
                <Beaker size={14} strokeWidth={1.5} /> Measured within the 2,008 mg blend
              </span>
            )}
            {ing.branded && <span className="label !text-[9.5px] px-2 py-0.5 border border-[color:var(--line)]">Branded active</span>}
          </div>

          <p className="mt-8 text-lg md:text-xl editorial text-[color:var(--ink)] leading-snug">{ing.benefit}</p>
          {ing.detail && <p className="mt-5 max-w-2xl text-[15px] leading-relaxed text-[color:var(--ink-2)]">{ing.detail}</p>}
        </div>

        {/* Right: the science card */}
        <div className="lg:col-span-5">
          {ing.study ? (
            <div className="border-2 border-[color:var(--ink)] bg-[color:var(--bg)]" data-testid="ingredient-study-card">
              <div className="bg-[#0f0f10] text-[#faf9f5] px-6 py-5">
                <div className="label !text-[9.5px] !text-[#faf9f5]/60">The Research</div>
                <div className="font-display text-xl tracking-[-0.01em] mt-1">{ing.study.label}</div>
              </div>
              <div className="p-6">
                <p className="text-[15px] leading-relaxed text-[color:var(--ink)]">{ing.study.summary}</p>
                {ing.study.citation && (
                  <div className="mt-5 pt-4 border-t border-[color:var(--line)]">
                    <div className="label !text-[9.5px] !text-[color:var(--ink-3)] mb-1">Citation</div>
                    <div className="text-[13px] italic text-[color:var(--ink-2)]">{ing.study.citation}</div>
                  </div>
                )}
                <a href={ing.study.url} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 h-11 px-5 bg-[#0f0f10] text-[#faf9f5] label !tracking-[0.2em] hover:bg-[#2a2a2a] transition" data-testid="ingredient-study-link">
                  Read the full study <ExternalLink size={13} strokeWidth={1.6} />
                </a>
              </div>
            </div>
          ) : (
            <div className="border border-[color:var(--line)] bg-[color:var(--bg-soft)] p-6" data-testid="ingredient-blend-note">
              <div className="flex items-center gap-2 label !text-[10px] !text-[color:var(--ink-3)] mb-3"><Beaker size={14} strokeWidth={1.5} /> Inside the blend</div>
              <p className="text-[15px] leading-relaxed text-[color:var(--ink-2)]">
                This active is measured within the trademark-pending Vitura Performance™ Blend (2,008 mg total). Every ingredient is named on the label in descending dose order.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Related */}
      <div className="mt-20 pt-10 border-t border-[color:var(--line)]">
        <div className="label !text-[10px] !text-[color:var(--ink-3)] mb-6">More from the blend</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[color:var(--line)] border border-[color:var(--line)]">
          {related.map((r) => (
            <Link key={r.slug} to={`/ingredients/${r.slug}`} className="group bg-[color:var(--bg)] p-6 hover:bg-[color:var(--bg-soft)] transition" data-testid={`related-${r.slug}`}>
              <div className="label !text-[9.5px] !text-[color:var(--ink-3)]">{r.category}</div>
              <div className="font-display text-lg tracking-[-0.01em] mt-2 leading-tight">{r.name.split("(")[0].trim()}</div>
              <span className="mt-4 inline-flex items-center gap-1 label !text-[9.5px] !tracking-[0.16em] group-hover:opacity-60 transition">
                Explore <ArrowUpRight size={11} strokeWidth={1.6} />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
